/**
 * Configuración de textos y colores para el módulo Returned
 *
 * Este archivo centraliza toda la lógica relacionada con:
 * - Estados de recepciones de devoluciones
 * - Colores de badges según estado
 * - Textos de botones según estado
 * - Colores de botones según estado
 *
 * @author Equipo de desarrollo
 * @version 1.0.0
 */

// 🎯 Estados de las recepciones de devoluciones según la API
export enum ReturnedStatus {
  CLOSED = 0, // Cerrado
  OPEN = 1, // Abierto
  PENDING = 2, // Por ubicar
  PENDING_OPEN = 3, // Pendiente (creado, sin pedidos procesados)
}

/**
 * Función para obtener el nombre del estado de recepción según el ID
 * @param status - ID del estado de recepción
 * @param t - Función de traducción
 * @returns Nombre del estado de recepción
 */
export const getReturnedStatusName = (status: ReturnedStatus, t: (key: string) => string): string => {
  switch (status) {
    case ReturnedStatus.CLOSED: // 0 = Cerrado
      return t('table.status.closed');
    case ReturnedStatus.OPEN: // 1 = Abierto
      return t('table.status.open');
    case ReturnedStatus.PENDING: // 2 = Por ubicar
      return t('table.status.pending');
    case ReturnedStatus.PENDING_OPEN: // 3 = Pendiente
      return t('table.status.pending_open');
    default:
      return t('table.status.unknown');
  }
};

/**
 * Obtiene la clase CSS del badge según el estado
 * @param status - ID del estado de recepción
 * @returns Clase CSS para el badge
 */
export const getReturnedStatusBadgeClass = (status: ReturnedStatus): string => {
  switch (status) {
    case ReturnedStatus.CLOSED: // 0 = Cerrado
      return 'bg-bg-success text-success'; // Verde - Proceso completado
    case ReturnedStatus.OPEN: // 1 = Abierto
      return 'bg-bg-info text-info'; // Azul - En proceso activo
    case ReturnedStatus.PENDING: // 2 = Por ubicar
      return 'bg-bg-warning text-warning'; // Naranja - Requiere atención
    case ReturnedStatus.PENDING_OPEN: // 3 = Pendiente
      return 'bg-card-border light:bg-bg-warning text-warning'; // Pendiente - Recién creado
    default:
      return 'bg-bg-error text-error'; // Rojo - Estado desconocido/error
  }
};

/**
 * Función para obtener el texto del botón de acción según el estado
 * @param status - ID del estado de recepción
 * @param t - Función de traducción
 * @returns Texto del botón
 */
export const getReturnedActionButtonText = (status: ReturnedStatus, t: (key: string) => string): string => {
  switch (status) {
    case ReturnedStatus.CLOSED: // 0 = Cerrado
      return t('table.actions.view');
    case ReturnedStatus.OPEN: // 1 = Abierto
      return t('table.actions.receive');
    case ReturnedStatus.PENDING: // 2 = Por ubicar
      return t('table.actions.locate');
    case ReturnedStatus.PENDING_OPEN: // 3 = Pendiente
      return t('table.actions.receive');
    default:
      return '';
  }
};

/**
 * Determina si se debe mostrar un botón de acción según el estado
 * @param status - ID del estado de recepción
 * @returns true si se debe mostrar un botón, false en caso contrario
 */
export const shouldShowReturnedActionButton = (status: ReturnedStatus): boolean => {
  switch (status) {
    case ReturnedStatus.CLOSED: // 0 = Cerrado
    case ReturnedStatus.OPEN: // 1 = Abierto
    case ReturnedStatus.PENDING: // 2 = Por ubicar
    case ReturnedStatus.PENDING_OPEN: // 3 = Pendiente
      return true;
    default:
      return false;
  }
};

/**
 * Obtiene el tipo de acción según el estado
 * @param status - ID del estado de recepción
 * @returns Tipo de acción ('view', 'locate', 'receive', o null)
 */
export const getReturnedActionType = (status: ReturnedStatus): 'view' | 'locate' | 'receive' | null => {
  switch (status) {
    case ReturnedStatus.CLOSED: // 0 = Cerrado
      return 'view';
    case ReturnedStatus.OPEN: // 1 = Abierto
      return 'receive';
    case ReturnedStatus.PENDING: // 2 = Por ubicar
      return 'locate';
    case ReturnedStatus.PENDING_OPEN: // 3 = Pendiente
      return 'receive';
    default:
      return null;
  }
};
